import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API, auth, PageHead, fmtDate } from '../organization/finance/shared';

const STATUS_LABELS = { pending_activation: 'بانتظار التفعيل', active: 'نشطة', suspended: 'معلقة', ended: 'منتهية' };
const STATUS_COLORS = { pending_activation: 'bg-amber-50 text-amber-700', active: 'bg-teal-50 text-teal-700', suspended: 'bg-orange-50 text-orange-700', ended: 'bg-slate-100 text-slate-500' };

export default function CoachOrganizationsPage() {
  const [memberships, setMemberships] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [scope, setScope] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { const res = await axios.get(`${API}/coaches/me/memberships`, auth()); setMemberships(res.data); } catch { setMemberships([]); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const viewScope = async (m) => {
    setSelected(m);
    setScope(null);
    if (m.status !== 'active') return;
    try { const res = await axios.get(`${API}/coaches/me/memberships/${m.id}/scope`, auth()); setScope(res.data); } catch { setScope({ teams: [], categories: [], players: [], matches: [] }); }
  };

  return (
    <div className="space-y-6" data-testid="coach-organizations-page">
      <PageHead title="المنشآت المرتبط بها" desc="جميع ارتباطاتك بالأندية والأكاديميات — الحالية والسابقة" />

      {loading ? <p className="text-sm text-slate-400">جارِ التحميل...</p> : memberships.length === 0 ? (
        <p className="rounded-2xl border border-dashed border-slate-200 bg-slate-50/60 py-10 text-center text-sm text-slate-400" data-testid="coach-memberships-empty">لا يوجد ارتباط بمنشآت حتى الآن</p>
      ) : (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <div className="space-y-2">
            {memberships.map((m) => (
              <button
                key={m.id}
                onClick={() => viewScope(m)}
                data-testid="coach-membership-row"
                className={`block w-full rounded-2xl border p-4 text-right shadow-[0_1px_3px_rgba(0,0,0,0.04)] transition-all active:scale-[0.98] ${selected?.id === m.id ? 'border-teal-400 bg-teal-50/40 ring-2 ring-teal-500/20' : 'border-slate-200/70 bg-white hover:border-teal-200'}`}
              >
                <div className="flex items-center justify-between">
                  <span className="text-sm font-bold text-slate-800">{m.club_name}</span>
                  <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${STATUS_COLORS[m.status]}`}>{STATUS_LABELS[m.status]}</span>
                </div>
                <div className="mt-1 text-xs text-slate-500">{m.title} {m.season && `· موسم ${m.season}`}</div>
                <div className="mt-1 text-[11px] text-slate-400">من {fmtDate(m.start_date)} {m.end_date ? `إلى ${fmtDate(m.end_date)}` : ''}</div>
              </button>
            ))}
          </div>

          <div className="rounded-2xl border border-slate-200/70 bg-white p-4 shadow-[0_1px_3px_rgba(0,0,0,0.04)] sm:p-5" data-testid="coach-membership-scope-panel">
            {!selected ? (
              <p className="text-sm text-slate-400">اختر منشأة لعرض الفرق واللاعبين والمباريات المسندة إليك</p>
            ) : selected.status !== 'active' ? (
              <p className="text-sm text-slate-400">هذا الارتباط غير نشط حالياً، لا يمكن عرض البيانات المسندة</p>
            ) : !scope ? (
              <p className="text-sm text-slate-400">جارِ التحميل...</p>
            ) : (
              <div className="space-y-4">
                <div>
                  <h4 className="text-xs font-bold uppercase text-slate-400">الفرق المسندة ({scope.teams.length})</h4>
                  <div className="mt-1 flex flex-wrap gap-2">{scope.teams.map((t) => <span key={t.id} className="rounded-full bg-slate-100 px-3 py-1 text-xs text-slate-700">{t.name}</span>)}</div>
                </div>
                <div>
                  <h4 className="text-xs font-bold uppercase text-slate-400">الفئات المسندة ({scope.categories.length})</h4>
                  <div className="mt-1 flex flex-wrap gap-2">{scope.categories.map((c) => <span key={c.id} className="rounded-full bg-slate-100 px-3 py-1 text-xs text-slate-700">{c.name}</span>)}</div>
                </div>
                <div>
                  <h4 className="text-xs font-bold uppercase text-slate-400">اللاعبون ({scope.players.length})</h4>
                  <ul className="mt-1 max-h-40 space-y-1 overflow-y-auto text-sm text-slate-700">{scope.players.map((p) => <li key={p.id}>{p.name}</li>)}</ul>
                </div>
                <div>
                  <h4 className="text-xs font-bold uppercase text-slate-400">المباريات ({scope.matches.length})</h4>
                  <ul className="mt-1 space-y-1 text-sm text-slate-700">{scope.matches.slice(0, 8).map((m) => <li key={m.id}>{fmtDate(m.match_date)}</li>)}</ul>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
